import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listUsers } from '../../services/authApi';
import { getCategoryTreeNodes, listCategoryTrees, listChannels } from '../../services/pimApi';
import {
  createAssignmentRule,
  deleteAssignmentRule,
  listAssignmentRules,
  listGates,
  reapplyAssignmentRules,
} from '../../services/workflowApi';
import { useSession } from '../auth/SessionProvider';
import './Admin.css';

/*
 * Category x gate -> approver routing.
 *
 * A rule covers its category node and everything beneath it, so mapping a
 * division routes the whole division. The most specific match wins: the
 * category nearest the product, then a market-specific rule over an all-market
 * one, then the lowest priority number.
 *
 * Routing is not permission. These rules decide whose queue an item lands in
 * and who is emailed; the role on the user's account is what allows them to
 * approve. An unrouted gate is still actionable by any role-holder.
 */

// The nodes endpoint returns a tree; the pick list wants one flat level.
function flatten(nodes, depth = 0, out = []) {
  nodes.forEach((n) => {
    out.push({ ...n, depth });
    flatten(n.children || [], depth + 1, out);
  });
  return out;
}

const labelOf = (node) => {
  const labels = node.labels || {};
  return labels.en_AU || labels.en_NZ || Object.values(labels)[0] || node.code;
};

export default function ApprovalRules() {
  const { isAdmin } = useSession();

  const [gates, setGates] = useState([]);
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [draft, setDraft] = useState({
    gate_code: '',
    category_id: '',
    channel_id: '',
    assignee_user_id: '',
    priority: 100,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, r, u, c] = await Promise.all([
        listGates(),
        listAssignmentRules(),
        listUsers(),
        listChannels().catch(() => []),
      ]);
      setGates(g);
      setRules(r);
      setUsers(u.filter((x) => x.enabled));
      setChannels((c || []).filter((ch) => ch.id !== 0));

      // Flatten every tree's nodes into one pick list, prefixed by tree so two
      // trees with similar node names stay distinguishable.
      const treeData = await listCategoryTrees().catch(() => ({}));
      const nodeLists = await Promise.all(
        (treeData.trees || []).map((t) =>
          getCategoryTreeNodes(t.code)
            .then((res) => flatten(res?.nodes || []).map((n) => ({ ...n, tree: t.code })))
            .catch(() => []),
        ),
      );
      setCategories(nodeLists.flat());
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load assignment rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const userName = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u.display_name || u.email]));
    return (id) => map.get(id) || `#${id}`;
  }, [users]);

  if (!isAdmin) {
    return (
      <div className="adm-page">
        <div className="apr-alert warn">
          Assignment rules need the admin role. Sign in with an administrator account.
        </div>
      </div>
    );
  }

  const addRule = async (e) => {
    e.preventDefault();
    if (!draft.gate_code || !draft.assignee_user_id) return;
    try {
      await createAssignmentRule({
        gate_code: draft.gate_code,
        assignee_user_id: Number(draft.assignee_user_id),
        category_id: draft.category_id ? Number(draft.category_id) : null,
        channel_id: draft.channel_id ? Number(draft.channel_id) : null,
        priority: Number(draft.priority) || 100,
      });
      setDraft({ ...draft, category_id: '', assignee_user_id: '' });
      setToast('Rule added. It applies to new gates; use “Re-route open items” for existing ones.');
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Could not create that rule.');
    }
  };

  const removeRule = async (id) => {
    try {
      await deleteAssignmentRule(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete that rule.');
    }
  };

  const reapply = async () => {
    try {
      const result = await reapplyAssignmentRules();
      setToast(
        `Re-routed ${result.reassigned} of ${result.examined} unclaimed gates. ` +
          'Claimed and decided items were left alone.',
      );
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not re-route.');
    }
  };

  return (
    <div className="adm-page">
      <header className="oc-header">
        <div className="oc-header-text">
          <h1>Approval routing</h1>
          <p>
            Map a category and gate to an approver. Rules cover everything beneath the category, and
            the most specific match wins. Routing decides whose queue an item lands in — the
            approver&apos;s role is what lets them sign it off.
          </p>
        </div>
        <div className="oc-header-action">
          <button type="button" className="oc-btn" onClick={reapply}>
            Re-route open items
          </button>
        </div>
      </header>

      {error && <div className="apr-alert bad">{error}</div>}
      {toast && <div className="apr-alert ok">{toast}</div>}

      <form className="adm-newrow adm-rulerow" onSubmit={addRule}>
        <select
          className="oc-input"
          value={draft.gate_code}
          onChange={(e) => setDraft({ ...draft, gate_code: e.target.value })}
        >
          <option value="">Gate…</option>
          {gates.map((g) => (
            <option key={g.code} value={g.code}>
              {g.label}
            </option>
          ))}
        </select>

        <select
          className="oc-input"
          value={draft.category_id}
          onChange={(e) => setDraft({ ...draft, category_id: e.target.value })}
        >
          <option value="">Every category (catch-all)</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tree} · {labelOf(c)}
            </option>
          ))}
        </select>

        <select
          className="oc-input"
          value={draft.channel_id}
          onChange={(e) => setDraft({ ...draft, channel_id: e.target.value })}
        >
          <option value="">All markets</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {c.market_code || c.code}
            </option>
          ))}
        </select>

        <select
          className="oc-input"
          value={draft.assignee_user_id}
          onChange={(e) => setDraft({ ...draft, assignee_user_id: e.target.value })}
        >
          <option value="">Approver…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name || u.email}
            </option>
          ))}
        </select>

        <button
          className="oc-btn oc-btn-primary"
          type="submit"
          disabled={!draft.gate_code || !draft.assignee_user_id}
        >
          Add rule
        </button>
      </form>

      {loading && <p className="apr-muted">Loading…</p>}

      <div className="oc-table-wrap">
        <table className="oc-table">
          <thead>
            <tr>
              <th>Gate</th>
              <th>Category</th>
              <th>Market</th>
              <th>Approver</th>
              <th>Priority</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td>{gates.find((g) => g.code === rule.gate_code)?.label || rule.gate_code}</td>
                <td>
                  {rule.category_code || <span className="apr-muted">every category</span>}
                </td>
                <td>{rule.market_code || <span className="apr-muted">all markets</span>}</td>
                <td>{rule.assignee_name || rule.assignee_email || userName(rule.assignee_user_id)}</td>
                <td>{rule.priority}</td>
                <td className="adm-actions">
                  <button type="button" className="oc-btn" onClick={() => removeRule(rule.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && rules.length === 0 && (
        <div className="oc-empty">
          <div className="oc-empty-title">No routing rules</div>
          <div className="oc-empty-sub">
            Gates are still reviewable — anyone holding the right role can pick them up. Add a rule
            to route them to a specific person.
          </div>
        </div>
      )}
    </div>
  );
}
